import {
	PipelineStageData,
	PipelineStageInputData,
	pipelineStageShowQueryKey,
	PipelineStageShowQueryResponse,
	useCampaignPipelineConfigDestroyStage,
	useCampaignPipelineConfigResetToDefaults,
	useCampaignPipelineConfigUpdate,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { v4 } from 'uuid'
import { logger } from '../../../../../ui'
import { keepEndStagesLast } from './-keep-end-stages-last'

export type UsePipelineStagesResult = {
	handleStageRename: (stageId: string, newName: string) => void
	handleStageAdd: (afterStageId: string, name: string) => void
	handleStageDelete: (stageId: string, migrateCandidatesTo?: string) => Promise<boolean>
	handleStagesReorder: (reorderedStages: Array<{ id: string; name: string; locked?: boolean }>) => void
	handleResetToDefaults: (migrations: Record<string, string>, onSuccess?: () => void) => void
	clearResetError: () => void
	getCandidateCounts: () => Record<string, number>
	getStages: () => PipelineStageData[]
	isDeleting: boolean
	isResetting: boolean
	resetErrorMessage: string | null
}

export function usePipelineStages(campaignId: string): UsePipelineStagesResult {
	const queryClient = useQueryClient()

	const pendingStagesRef = useRef<PipelineStageInputData[] | null>(null)
	const unsavedStageIdsRef = useRef<Set<string>>(new Set())
	const pendingDeleteCountRef = useRef(0)
	const deletingStageIdsRef = useRef<Set<string>>(new Set())

	const getStages = useCallback((): PipelineStageData[] => {
		const data = queryClient.getQueryData<PipelineStageShowQueryResponse>(
			pipelineStageShowQueryKey(campaignId),
		)
		return data?.pipelineStages ?? []
	}, [queryClient, campaignId])

	const setStagesOptimistically = useCallback((stages: PipelineStageInputData[]) => {
		queryClient.setQueryData<PipelineStageShowQueryResponse>(
			pipelineStageShowQueryKey(campaignId),
			(old) => {
				if (!old) { return undefined }
				return {
					...old,
					pipelineStages: stages as PipelineStageShowQueryResponse['pipelineStages'],
				}
			},
		)
	}, [queryClient, campaignId])

	const { mutateAsync: updatePipelineConfigAsync } = useCampaignPipelineConfigUpdate({
		onError: (error) => {
			logger.error(error, {
				category: 'pipeline',
				data: { campaignId, action: 'update' },
			})
			unsavedStageIdsRef.current.clear()
			void queryClient.invalidateQueries({
				queryKey: pipelineStageShowQueryKey(campaignId),
			})
		},
		onSuccess: async () => {
			unsavedStageIdsRef.current.clear()
			await queryClient.refetchQueries({
				queryKey: pipelineStageShowQueryKey(campaignId),
			})
		},
	})

	const flushPendingUpdate = useMemo(
		() =>
			debounce(async () => {
				const pending = pendingStagesRef.current
				if (!pending) {
					return
				}
				pendingStagesRef.current = null
				await updatePipelineConfigAsync({
					campaign: campaignId,
					data: { stages: pending },
				})
			}, 500),
		[updatePipelineConfigAsync, campaignId],
	)

	useEffect(() => {
		return () => {
			void flushPendingUpdate.flush()
		}
	}, [flushPendingUpdate])

	const scheduleUpdate = useCallback((requestedStages: PipelineStageInputData[]) => {
		const stages = keepEndStagesLast(requestedStages)
		pendingStagesRef.current = stages
		setStagesOptimistically(stages)
		void flushPendingUpdate()
	}, [setStagesOptimistically, flushPendingUpdate])

	const { mutateAsync: deleteStageApi, isPending: isDeleting } = useCampaignPipelineConfigDestroyStage({
		onMutate: async ({ stageId }) => {
			pendingDeleteCountRef.current++
			await queryClient.cancelQueries({ queryKey: pipelineStageShowQueryKey(campaignId) })

			const previousData = queryClient.getQueryData<PipelineStageShowQueryResponse>(
				pipelineStageShowQueryKey(campaignId),
			)

			const stages = getStages()
			const updatedStages = stages.filter(s => s.id !== stageId)
			setStagesOptimistically(updatedStages)

			return { previousData }
		},
		onError: (error, variables, context) => {
			logger.error(error, {
				category: 'pipeline',
				data: {
					campaignId,
					action: 'delete',
					stageId: variables.stageId,
				},
			})
			const previousData = (context as { previousData?: PipelineStageShowQueryResponse } | undefined)
				?.previousData
			if (previousData) {
				queryClient.setQueryData(pipelineStageShowQueryKey(campaignId), previousData)
			}
		},
		onSettled: (_data, _error, variables) => {
			deletingStageIdsRef.current.delete(variables.stageId)
			pendingDeleteCountRef.current--
			if (pendingDeleteCountRef.current === 0) {
				void queryClient.invalidateQueries({
					queryKey: pipelineStageShowQueryKey(campaignId),
				})
			}
		},
	})

	const getCandidateCounts = useCallback((): Record<string, number> => {
		const data = queryClient.getQueryData<PipelineStageShowQueryResponse>(
			pipelineStageShowQueryKey(campaignId),
		)
		const candidates = data?.candidates ?? []
		const counts: Record<string, number> = {}
		for (const candidate of candidates) {
			const stageId = candidate.pipelineStageId
			if (stageId) {
				counts[stageId] = (counts[stageId] ?? 0) + 1
			}
		}
		return counts
	}, [queryClient, campaignId])

	const handleStageRename = useCallback((stageId: string, newName: string) => {
		const stages = getStages()
		const updatedStages = stages.map(stage => stage.id === stageId ? { ...stage, name: newName } : stage)
		scheduleUpdate(updatedStages)
	}, [getStages, scheduleUpdate])

	const handleStageAdd = useCallback((afterStageId: string, name: string) => {
		const stages = getStages()
		const afterIndex = stages.findIndex(s => s.id === afterStageId)
		if (afterIndex === -1) {
			logger.warn('[usePipelineStages] afterStageId not found', {
				category: 'pipeline',
				data: { afterStageId, stages },
			})
			return
		}

		const newStageId = v4()
		const newStage: PipelineStageInputData = {
			id: newStageId,
			name,
			locked: false,
		}

		unsavedStageIdsRef.current.add(newStageId)

		const updatedStages = [
			...stages.slice(0, afterIndex + 1),
			newStage,
			...stages.slice(afterIndex + 1),
		]
		scheduleUpdate(updatedStages)
	}, [getStages, scheduleUpdate])

	// Returns true only when a stage was actually removed, so callers fire
	// pipeline:stage_deleted once per real deletion.
	const handleStageDelete = useCallback(async (stageId: string, migrateCandidatesTo?: string): Promise<boolean> => {
		// Skip a duplicate delete of the same in-flight stage. The flush below is async, so
		// a second click would otherwise fire a DELETE for an already-removed stage (404).
		if (deletingStageIdsRef.current.has(stageId)) {
			return false
		}

		if (unsavedStageIdsRef.current.has(stageId)) {
			unsavedStageIdsRef.current.delete(stageId)

			if (pendingStagesRef.current) {
				pendingStagesRef.current = pendingStagesRef.current.filter(s => s.id !== stageId)
			}

			const stages = getStages()
			const updatedStages = stages.filter(s => s.id !== stageId)
			setStagesOptimistically(updatedStages)
			return true
		}

		deletingStageIdsRef.current.add(stageId)

		if (pendingStagesRef.current) {
			try {
				await flushPendingUpdate.flush()
			} catch {
				deletingStageIdsRef.current.delete(stageId)
				return false
			}
		}

		// Deleting a stage migrates its candidates, so the caller must not treat it
		// as done until the request lands. A rejection propagates: both call sites
		// await inside a try, and keep their confirmation dialog open on failure.
		await deleteStageApi({
			campaign: campaignId,
			stageId,
			params: migrateCandidatesTo ? { migrateCandidatesTo } : {},
		})

		return true
	}, [getStages, setStagesOptimistically, flushPendingUpdate, deleteStageApi, campaignId])

	const handleStagesReorder = useCallback(
		(reorderedStages: Array<{ id: string; name: string; locked?: boolean }>) => {
			scheduleUpdate(reorderedStages)
		},
		[scheduleUpdate],
	)

	const {
		mutate: resetToDefaultsMutation,
		isPending: isResetting,
		error: resetError,
		reset: clearResetError,
	} = useCampaignPipelineConfigResetToDefaults({
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: pipelineStageShowQueryKey(campaignId),
			})
		},
		onError: (error) => {
			logger.error(error, {
				category: 'pipeline',
				data: { campaignId, action: 'resetToDefaults' },
			})
			void queryClient.invalidateQueries({
				queryKey: pipelineStageShowQueryKey(campaignId),
			})
		},
	})

	const resetErrorMessage = resetError
		? resetError.response?.data?.message ?? 'Something went wrong while resetting the pipeline. Please try again.'
		: null

	const handleResetToDefaults = useCallback((
		migrations: Record<string, string>,
		onSuccess?: () => void,
	) => {
		resetToDefaultsMutation(
			{
				campaign: campaignId,
				// Backend accepts Record<string, string> but OpenAPI spec generates string[] — Scramble doesn't map PHP's array<string, string> correctly
				data: { migrations },
			},
			{ onSuccess },
		)
	}, [resetToDefaultsMutation, campaignId])

	return {
		handleStageRename,
		handleStageAdd,
		handleStageDelete,
		handleStagesReorder,
		handleResetToDefaults,
		clearResetError,
		getCandidateCounts,
		getStages,
		isDeleting,
		isResetting,
		resetErrorMessage,
	}
}
